import { db } from "@/lib/db";
import { getPlanFeatures, type SubscriptionPlan } from "@/lib/subscription/plans";

export interface UsageRecord {
  id: string;
  userId: string;
  profileId?: string; // Optional - some actions don't have a profile
  timestamp: number;
  action: "message" | "api_call" | "file_upload" | "knowledge_index" | "builder_action" | "ai_created" | "conversation_created" | "project_created";
  metadata?: Record<string, any>;
}

// Use plan features for rate limits (single source of truth)
function getRateLimits(plan: SubscriptionPlan) {
  const features = getPlanFeatures(plan);
  return {
    messages_per_day: features.messagesPerDay,
    api_calls_per_day: features.apiCallsPerDay,
    file_uploads_per_day: features.fileUploadsPerDay,
  };
}

// Legacy rate limits (for backward compatibility)
const RATE_LIMITS = {
  free: {
    messages_per_day: 100,
    api_calls_per_day: 50,
    file_uploads_per_day: 10,
  },
  pro: {
    messages_per_day: 1000,
    api_calls_per_day: 500,
    file_uploads_per_day: 100,
  },
  pro_plus: {
    messages_per_day: 10000,
    api_calls_per_day: 5000,
    file_uploads_per_day: 1000,
  },
  enterprise: {
    messages_per_day: 100000,
    api_calls_per_day: 50000,
    file_uploads_per_day: 10000,
  },
};

export async function trackUsage(
  userId: string,
  profileId: string,
  action: UsageRecord["action"],
  metadata?: Record<string, any>
): Promise<void> {
  try {
    if (typeof window === "undefined" || !db || !db.usageRecords) {
      return; // Skip tracking on server-side or if DB not available
    }

    const record: UsageRecord = {
      id: crypto.randomUUID(),
      userId,
      profileId,
      timestamp: Date.now(),
      action,
      metadata,
    };

    await db.usageRecords.put(record);
  } catch (error) {
    console.error("Failed to track usage:", error);
    // Don't throw - tracking failures shouldn't break the app
  }
}

export async function checkRateLimit(
  userId: string,
  plan: "free" | "pro" | "pro_plus" | "enterprise" = "free",
  action: "message" | "api_call" | "file_upload"
): Promise<{ allowed: boolean; remaining: number; limit: number }> {
  // Use plan features for rate limits
  const limits = getRateLimits(plan as SubscriptionPlan);
  const limitKey = `${action}_per_day` as keyof typeof limits;
  const limit = limits[limitKey] || 100;

  // Get today's usage
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const todayStart = today.getTime();

  // Count usage records for today
  let allRecords: any[] = [];
  try {
    if (typeof window !== "undefined" && db && db.usageRecords) {
      allRecords = await db.usageRecords
        .where("userId")
        .equals(userId)
        .toArray();
    }
  } catch (error) {
    console.error("Failed to check rate limit:", error);
    // If we can't check, allow the request (fail open)
    return {
      allowed: true,
      remaining: limit,
      limit,
    };
  }
  
  const usageRecords = allRecords.filter(
    (record: any) => record.timestamp >= todayStart && record.action === action
  );

  const used = usageRecords.length;
  const remaining = Math.max(0, limit - used);

  return {
    allowed: used < limit,
    remaining,
    limit,
  };
}

export async function getUsageStats(
  userId: string,
  period: "day" | "week" | "month" = "day"
): Promise<{
  messages: number;
  apiCalls: number;
  fileUploads: number;
  knowledgeIndexes: number;
}> {
  const now = Date.now();
  let periodStart: number;

  switch (period) {
    case "day":
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      periodStart = today.getTime();
      break;
    case "week":
      periodStart = now - 7 * 24 * 60 * 60 * 1000;
      break;
    case "month":
      periodStart = now - 30 * 24 * 60 * 60 * 1000;
      break;
  }

  let allRecords: any[] = [];
  try {
    if (typeof window !== "undefined" && db && db.usageRecords) {
      allRecords = await db.usageRecords
        .where("userId")
        .equals(userId)
        .toArray();
    }
  } catch (error) {
    console.error("Failed to get usage stats:", error);
    // Return zeros if we can't get stats
    return {
      messages: 0,
      apiCalls: 0,
      fileUploads: 0,
      knowledgeIndexes: 0,
    };
  }
  
  const usageRecords = allRecords.filter((record: any) => record.timestamp >= periodStart);

  return {
    messages: usageRecords.filter((r: any) => r.action === "message").length,
    apiCalls: usageRecords.filter((r: any) => r.action === "api_call").length,
    fileUploads: usageRecords.filter((r: any) => r.action === "file_upload").length,
    knowledgeIndexes: usageRecords.filter((r: any) => r.action === "knowledge_index").length,
  };
}
